import React from 'react';
import { Link } from 'react-router-dom';
import '../../styles/components/CaseStudyCard.css';

/**
 * Quiet, cohesive case-study card.
 *
 * Visual area = grainy pastel background derived from the brand color,
 * with the brand logo centered. Below sits the metadata (title, role,
 * period). The grain texture is overlaid via a CSS pseudo-element
 * using an inline SVG fractal-noise filter — no external image asset
 * needed, no extra HTTP request.
 *
 * Props:
 *   logo     — image src (SVG/PNG) for the brand logo
 *   bgColor  — pastel hex for the visual background
 *   title    — project / company name
 *   role     — sub-line below title (e.g. "Full-time · Product Designer")
 *   period   — date range or note (e.g. "Aug 2025 — Apr 2026")
 *   to       — internal route (uses <Link>) — pass either `to` OR `href`
 *   href     — external URL (uses <a target="_blank">)
 *   logoMaxWidth — optional cap on logo width (px); default 200
 */
const CaseStudyCard = ({
  logo,
  bgColor,
  title,
  role,
  period,
  to,
  href,
  logoMaxWidth = 200,
  logoMaxHeight,
  passwordProtected = false,
  whiteBg = false,
}) => {
  const inner = (
    <>
      <div
        className="case-study-card__visual"
        style={{ backgroundColor: bgColor }}
      >
        <img
          src={logo}
          alt={`${title} logo`}
          className={`case-study-card__logo${whiteBg ? ' case-study-card__logo--white-bg' : ''}`}
          style={{
            maxWidth: logoMaxWidth,
            maxHeight: logoMaxHeight || '70%',
          }}
          loading="lazy"
        />
      </div>
      <div className="case-study-card__meta">
        {role && <p className="case-study-card__role">{role}</p>}
        <div className="case-study-card__sub">
          {period && <span className="case-study-card__period">{period}</span>}
          {passwordProtected && (
            <span className="case-study-card__lock" title="Password protected">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Password protected
            </span>
          )}
        </div>
      </div>
    </>
  );

  if (to) {
    return (
      <Link to={to} className="case-study-card">
        {inner}
      </Link>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="case-study-card"
    >
      {inner}
    </a>
  );
};

export default CaseStudyCard;
